import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { has, isArray, isEmpty, isEqual, isNull, omit } from 'lodash-es'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { ScreenContainerRef } from '../../../context'
import { useCopyToClipboard, useNotification, useScreen } from '../../../hooks'
import { getHost } from '../../../utils/api'
import {
  getListType,
  getPermanentFilters as getCommonPermamentFilters,
  getVisibleFilters,
  isCustomFilterType,
  isDateFilterType,
  isMultiselectFilterType,
  isNumberRangeFilterType,
  isRatingFilterName,
  isSelectFilterType,
  isToggleFilterType,
} from '../../../utils/filter'
import Button, { BUTTON_HEIGHT_32 } from '../../Button'
import { CustomSelect } from '../../CustomSelect'
import { ICON_HEIGHT_16 } from '../../Icon'
import { getFilterTemplates } from '../Filter.api'
import { EXCLUDE_FILTERS } from '../Filter.constants'
import { FilterBarClearFiltersButton } from '../FilterBarClearFiltersButton'
import { FilterBarItems } from '../FilterBarItems'
import { FilterDate } from '../FilterDate'
import { FilterModal } from '../FilterModal'
import { getFilterSelectTypeValues } from '../FilterModalAvailableFilters'
import { FilterNumberRange } from '../FilterNumberRange'
import { FilterSaveModal } from '../FilterSaveModal'
import { FilterToggle } from '../FilterToggle'

import { shareFilterStates } from './FilterBar.api'
import {
  FILTER_BAR_FILTER_TRIGGER_VARIANT,
  FILTER_TEMPLATE_DROPDOWN_MAX_WIDTH,
  QA_NAME_FILTERS_BTN,
  QA_NAME_FILTERS_COMPACT_CLEAR_BTN,
  RATING_OPTIONS,
} from './FilterBar.constants'
import { useFilterBarLoadData, useFilterBarReload } from './FilterBar.hooks'
import {
  checkIsCompactFilterTrigger,
  getAfterClearedFilterOptions,
  getDateOption,
  getFilterData,
  getNextFilterOptions,
  getNextFilterOptionsByDate,
  getNextFilterParams,
  getTemplateLabel,
  getTemplateValue,
  parsePermanentCommonFilters,
} from './FilterBar.utils'
import { CompactFilterSettingsIcon } from './FilterBarCompactFilterIcon'
import { FilterBarMultiSelect } from './FilterBarMultiSelect'

import * as styles from './FilterBar.module.css'

import FilterSettingsIcon from '../../../icons/filter-settings.svg'
import TemplateIcon from '../../../icons/template.svg'

function FilterBar({
  apiUrl,
  availableFilters,
  buttonsClassName = styles.buttons,
  canViewFilterTemplate,
  className = styles.filterBar,
  containerRef,
  disabled,
  entity,
  excludeFilters = EXCLUDE_FILTERS,
  filterBarItemsProps,
  filterTriggerVariant = FILTER_BAR_FILTER_TRIGGER_VARIANT.DEFAULT,
  filterValues,
  getPermanentFilters = getCommonPermamentFilters,
  hideFilterTemplateSkeleton,
  initialRangeFilterNames = EMPTY_LIST,
  isHidden,
  isSkeleton,
  objectId,
  ordering = EMPTY_LIST,
  pagination = EMPTY_OBJECT,
  parsePermanentFilters = parsePermanentCommonFilters,
  responsive,
  setFilterValues,
  shareBodyExtras = EMPTY_OBJECT,
  shareEntity,
  shouldReloadValues,
  singleFieldApiParams = EMPTY_OBJECT,
  sub,
  onChangeShouldReloadValues,
  onReload,
}) {
  const [isEditing, setEditing] = useState(false)
  const [isFiltersOpen, setFiltersOpen] = useState(false)
  const [isSaving, setSaving] = useState(false)
  const [prevIsFiltersOpen, setPrevIsFiltersOpen] = useState(isFiltersOpen)

  // leaves edit mode when the filters close, during render
  if (isFiltersOpen !== prevIsFiltersOpen) {
    setPrevIsFiltersOpen(isFiltersOpen)

    if (!isFiltersOpen) {
      setEditing(false)
    }
  }

  const { isMobileScreenSize } = useScreen({ containerRef })

  const shouldAdaptToMobileScreen = isMobileScreenSize && responsive

  const [value, setValue] = useState(null)

  const permanentFilters = getPermanentFilters?.({ filterValues, sub })

  const isCompactFilterTrigger =
    checkIsCompactFilterTrigger(filterTriggerVariant)

  const hasActiveFiltersBeyondPermanentValue = !isEmpty(
    omit(filterValues, excludeFilters)
  )

  const { t } = useTranslation()

  const [, copy] = useCopyToClipboard()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const {
    filterOptions,
    handleChangeFilterOptions,
    isInitiated,
    loadFilterBarData,
  } = useFilterBarLoadData({
    apiUrl,
    availableFilters,
    entity,
    excludeFilters,
    filterValues,
    parsePermanentFilters,
    permanentFilters,
    onClear: handleClear,
  })

  useEffect(() => {
    // should load filter bar data only if available filters are present
    // to prevent breaking date filters
    if (availableFilters.length > 0) {
      loadFilterBarData()
    }
  }, [availableFilters, filterValues])

  function handleApply(options) {
    const params = getNextFilterParams(options, filterOptions)

    const currentFilters = omit(filterValues, excludeFilters)

    if (!isEqual(params, currentFilters)) {
      setFilterValues({ ...params, ...permanentFilters })
    }
  }

  useEffect(() => {
    if (isInitiated) {
      handleApply()
    }
  }, [isInitiated])

  useFilterBarReload({
    handleApply,
    loadFilterBarData,
    shouldReloadValues,
    onChangeShouldReloadValues,
    onReload,
  })

  function handleCloseFilters() {
    setFiltersOpen(false)
    setValue(null) // reset value filters after closing filter modals
  }

  function handleOpenFilters() {
    setFiltersOpen(true)
  }

  function handleClear(filterKey, additionalKey) {
    const nextFilterOptions = getAfterClearedFilterOptions({
      additionalKey,
      filterKey,
      filterOptions,
      permanentFilters,
    })

    setNextOption(nextFilterOptions)
  }

  function handleClearAll() {
    handleClear()
  }

  function setNextOption(nextOptions) {
    handleChangeFilterOptions(nextOptions)
    handleApply(nextOptions)
  }

  function getHandleChangeBaseFilterOptions(params) {
    return value => {
      const nextFilterOptions = getNextFilterOptions({
        value,
        ...params,
        filterOptions,
      })

      setNextOption(nextFilterOptions)
    }
  }

  function getHandleChangeDate(params) {
    return value => {
      const nextFilterOptions = getNextFilterOptionsByDate({
        endParam: params[1],
        filterOptions,
        startParam: params[0],
        value,
      })

      setNextOption(nextFilterOptions)
    }
  }

  function getHandleClearNumberRange(params) {
    return () => {
      setNextOption(omit(filterOptions, params))
    }
  }

  function handleSetRangeDate(startDateKey, endDateKey) {
    return dateValues => {
      const [startValue, endValue] = isArray(dateValues)
        ? dateValues
        : [null, null]

      const nextOptions = getDateOption({
        key: startDateKey,
        nextOptionsProps: {
          key: endDateKey,
          value: endValue,
        },
        options: filterOptions,
        value: startValue,
      })

      setNextOption(nextOptions)
    }
  }

  function getItems(search, apiParams) {
    const params = {
      ...apiParams,
      ...singleFieldApiParams,
      search,
    }

    return getFilterData({
      ...params,
      apiUrl,
      entity,
      parsePermanentFilters,
      permanentFilters,
    })
  }

  function getValue(option) {
    return option.value
  }

  const tableItems = filterOptions
    ? availableFilters
        .map(item => {
          if (
            !has(filterOptions, item.name) &&
            !has(filterOptions, `${item.name}_gte`) &&
            !has(filterOptions, `${item.name}_lte`)
          ) {
            return null
          }

          const listType = getListType(item, entity)

          if (isSelectFilterType(item.type)) {
            return (
              <CustomSelect
                showBtnTextTooltip
                activeItems={getFilterSelectTypeValues(
                  item.name,
                  filterOptions
                )}
                // Static options resolve locally, not via the endpoint.
                // TODO: drop `options` once every facet is served by the endpoint.
                api={item.options ? undefined : getItems}
                apiParams={{ field_name: item.single_field_query_param }}
                buttonHeight={32}
                buttonType='white'
                className={styles.tableSelect}
                entity={entity}
                filterKey={item.query_param}
                getValue={getValue}
                inputClassName={styles.input}
                isSkeleton={isSkeleton}
                key={item.name}
                listType={listType}
                menuClassName={styles.menu}
                options={item.options}
                placeholder={t(item.title)} // changed to item.title cause of chips name and should be reworked https://idwell.atlassian.net/browse/IRD-1499
                searchKey={item.search_param}
                tags={filterOptions && [filterOptions[item.name]]}
                onClear={getHandleChangeBaseFilterOptions({
                  key: item.name,
                  value: null,
                })}
                onClick={getHandleChangeBaseFilterOptions({ key: item.name })}
              />
            )
          }

          if (isMultiselectFilterType(item.type)) {
            return (
              <FilterBarMultiSelect
                entity={entity}
                filterOptions={filterOptions}
                getItems={getItems}
                isSkeleton={isSkeleton}
                item={item}
                key={item.name}
                listType={listType}
                onApply={handleApply}
              />
            )
          }

          if (isToggleFilterType(item.type)) {
            return (
              <FilterToggle
                className={styles.tableToggle}
                filter={item}
                isOn={filterOptions && filterOptions[item.name]}
                key={item.name}
                onClick={getHandleChangeBaseFilterOptions({ key: item.name })}
              />
            )
          }

          if (isCustomFilterType(item.type)) {
            if (isRatingFilterName(item.name)) {
              return (
                <CustomSelect
                  isMulti
                  showBtnTextTooltip
                  accordionTitle={t(item.title)}
                  activeItems={
                    filterOptions &&
                    filterOptions[item.name] &&
                    filterOptions[item.name].length > 0
                      ? filterOptions[item.name].map(i => i.value)
                      : EMPTY_LIST
                  }
                  buttonClassName={styles.button}
                  buttonHeight={40}
                  buttonType='outlined'
                  filterKey={item.query_param}
                  inputClassName={styles.input}
                  key={item.name}
                  listType={listType}
                  options={RATING_OPTIONS}
                  placeholder={t(item.placeholder)}
                  searchKey='search'
                  tags={filterOptions && filterOptions[item.name]}
                  onClick={getHandleChangeBaseFilterOptions({
                    isMulti: true,
                    key: item.name,
                  })}
                />
              )
            }
          }

          if (isNumberRangeFilterType(item.type)) {
            const [startParam, endParam] = item.query_param

            return (
              <FilterNumberRange
                key={item.name}
                label={item.title}
                max={filterOptions[endParam]}
                min={filterOptions[startParam]}
                qaName={item.name}
                onClear={getHandleClearNumberRange([startParam, endParam])}
                onClick={handleOpenFilters}
              />
            )
          }

          if (isDateFilterType(item.type)) {
            const startParam = item.query_param[0]
            const endParam = item.query_param[1]

            const start = filterOptions[startParam]?.value
            const end = filterOptions[endParam]?.value

            return (
              <FilterDate
                controlProps={{
                  initialIsRange: initialRangeFilterNames.includes(item.name),
                }}
                endDate={end}
                key={item.name}
                label={item.title}
                startDate={start}
                onChange={getHandleChangeDate(item.query_param)}
                onChangeRange={handleSetRangeDate(startParam, endParam)}
              />
            )
          }

          return null
        })
        .filter(item => !isNull(item))
    : null

  function getTemplates(search, apiParams) {
    const params = {
      ...(search ? { search } : undefined),
      table_name: entity.plural,
    }

    return getFilterTemplates({
      ...apiParams,
      ...params,
    })
  }

  function handleSetTemplate(option) {
    getFilterTemplates(null, option.id).then(data => {
      setFilterValues({ ...permanentFilters, ...data.data.filter_values })

      handleAddSuccessNotification('Notification.CommonTitleAppliedKey')
    })
  }

  function handleEdit(option) {
    setEditing(true)
    setFiltersOpen(true)
    setValue(option)
  }

  function handleSaveTemplate() {
    setSaving(true)
  }

  function handleCloseSaving() {
    setSaving(false)
  }

  async function handleShare() {
    shareFilterStates({
      bodyData: {
        ...shareBodyExtras,
        filters: filterValues,
        ordering,
        page: 1,
        pagination,
      },
      entity: shareEntity || entity,
      objectId,
      sub,
    })
      .then(({ data: { key } }) => {
        const host = getHost()

        return copy(`${host}filter-state/${key}`)
      })
      .then(copied => {
        if (copied) {
          handleAddSuccessNotification(
            'Notification.FilterCopiedToClipboardTitleKey'
          )
        }
      })
      .catch(() =>
        handleAddErrorNotification(
          'Notification.FilterErrorCopiedToClipboardTitleKey'
        )
      )
  }

  const shouldRenderFilterBar =
    isSkeleton || getVisibleFilters(availableFilters).length > 0

  return (
    <>
      {shouldRenderFilterBar ? (
        <div
          className={classNames(className, {
            [styles.hideFilterBar]: isHidden, // TODO: Remove this “hideFilterBar” hack by fixing initial-load filter template when filter values don’t match any items
          })}
        >
          <div className={styles.row}>
            <div className={buttonsClassName}>
              <Button.White
                isGrey
                shadowed
                beforeIconProps={{
                  Icon: isCompactFilterTrigger
                    ? CompactFilterSettingsIcon
                    : FilterSettingsIcon,
                  height: ICON_HEIGHT_16,
                  iconProps: isCompactFilterTrigger
                    ? { hasActiveFilters: hasActiveFiltersBeyondPermanentValue }
                    : undefined,
                }}
                disabled={disabled}
                height={BUTTON_HEIGHT_32}
                isSkeleton={isSkeleton}
                qaName={QA_NAME_FILTERS_BTN}
                text={
                  isCompactFilterTrigger || shouldAdaptToMobileScreen
                    ? EMPTY_STRING
                    : t('Table.FilterBtnKey')
                }
                tooltip={t('Table.FilterBtnTooltipKey')}
                onClick={handleOpenFilters}
              />
              {canViewFilterTemplate ||
              (isSkeleton && !hideFilterTemplateSkeleton) ? (
                <CustomSelect // TODO after refactor filter template select, change using  hook useScreen to css only
                  api={getTemplates}
                  beforeIconProps={{
                    Icon: shouldAdaptToMobileScreen ? TemplateIcon : null,
                    height: ICON_HEIGHT_16,
                  }}
                  buttonType='white'
                  className={
                    shouldAdaptToMobileScreen
                      ? undefined
                      : styles.templateSelect
                  }
                  dropdownClassName={styles.dropdown}
                  dropdownMaxWidth={
                    shouldAdaptToMobileScreen
                      ? FILTER_TEMPLATE_DROPDOWN_MAX_WIDTH
                      : undefined
                  }
                  getLabel={getTemplateLabel}
                  getValue={getTemplateValue}
                  isSkeleton={isSkeleton}
                  listType='filter'
                  noDataText={t('Table.SelectNoDataTextKey')}
                  placeholder={
                    shouldAdaptToMobileScreen
                      ? EMPTY_STRING
                      : t('Table.FilterTemplateBtnKey')
                  }
                  searchKey='search'
                  onClick={handleSetTemplate}
                  onEdit={handleEdit}
                />
              ) : null}
              {isCompactFilterTrigger &&
              hasActiveFiltersBeyondPermanentValue ? (
                <FilterBarClearFiltersButton
                  isSkeleton={isSkeleton}
                  qaName={QA_NAME_FILTERS_COMPACT_CLEAR_BTN}
                  onClick={handleClearAll}
                />
              ) : null}
            </div>
            {!isCompactFilterTrigger && tableItems?.length > 0 ? (
              <FilterBarItems
                {...filterBarItemsProps}
                canViewFilterTemplate={canViewFilterTemplate}
                isSkeleton={isSkeleton}
                items={tableItems}
                onClear={handleClearAll}
                onSaveTemplate={handleSaveTemplate}
                onShare={handleShare}
              />
            ) : null}
          </div>
        </div>
      ) : null}
      {isFiltersOpen ? (
        <ScreenContainerRef value={null}>
          <FilterModal
            apiUrl={apiUrl}
            availableFilters={availableFilters}
            canViewFilterTemplate={canViewFilterTemplate}
            entity={entity}
            excludeFilters={excludeFilters}
            filterValues={filterValues}
            initialRangeFilterNames={initialRangeFilterNames}
            isEditing={isEditing}
            ordering={ordering}
            pagination={pagination}
            parsePermanentFilters={parsePermanentFilters}
            permanentFilters={permanentFilters}
            responsive={responsive} // TODO remove this flag after making responsive all filter modals on project, and make responsive by css
            setFilterValues={setFilterValues}
            singleFieldApiParams={singleFieldApiParams}
            sub={sub}
            value={value}
            onClose={handleCloseFilters}
          />
        </ScreenContainerRef>
      ) : null}
      {isSaving ? (
        <FilterSaveModal
          entity={entity}
          filterOptions={filterOptions}
          onClose={handleCloseSaving}
        />
      ) : null}
    </>
  )
}

export default FilterBar
